import { useState } from "react";
import { supabase } from "../supabase";

/**
 * The "set a new password" screen shown after a user clicks a reset link from
 * their email. Supabase turns that link into a short-lived recovery session, so
 * by the time this renders the user is authenticated *only* for the purpose of
 * choosing a new password — App gates on the recovery flag to show this instead
 * of the dashboard.
 *
 * On success the account keeps that (now-authenticated) session, so `onDone`
 * simply drops the recovery flag and the app continues signed in. `onCancel`
 * abandons the reset and signs back out to the login screen.
 */
export function ResetPassword({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // Match the account-settings policy (Security.tsx) so the two paths agree.
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      // Strip the recovery token from the URL so a refresh can't re-enter this
      // flow with a now-spent token.
      try {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      } catch {
        // Non-fatal: some embedded contexts disallow history writes.
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't update your password.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="center">
        <div className="card login">
          <img className="brand-mark big" src="/logo.png" alt="" width={56} height={56} />
          <h1>Password updated</h1>
          <div className="auth-notice" role="status">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <span>Your password has been changed — you’re signed in and ready to go.</span>
          </div>
          <button className="primary" onClick={onDone}>
            Continue to Broadside
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="center">
      <form className="card login" onSubmit={submit}>
        <img className="brand-mark big" src="/logo.png" alt="" width={56} height={56} />
        <h1>Set a new password</h1>
        <p className="muted">Choose a new password for your Broadside account.</p>
        <input
          type="password"
          placeholder="New password"
          value={password}
          autoComplete="new-password"
          autoFocus
          onChange={(e) => setPassword(e.target.value)}
        />
        <input
          type="password"
          placeholder="Confirm new password"
          value={confirm}
          autoComplete="new-password"
          onChange={(e) => setConfirm(e.target.value)}
        />
        {error && <p className="error">{error}</p>}
        <button className="primary" disabled={busy || !password || !confirm}>
          {busy ? "…" : "Update password"}
        </button>
        <p className="muted small" style={{ marginTop: 14 }}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
          >
            Cancel
          </a>
        </p>
      </form>
    </div>
  );
}
